import { jsx } from "react/jsx-runtime";
import { createContext, useContext } from "react";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
function createServiceHTTPClientProvider({
  name
}) {
  const Context = createContext(null);
  function ServiceHTTPClientProvider({
    client,
    children
  }) {
    return /* @__PURE__ */ jsx(Context.Provider, { value: client, children });
  }
  ServiceHTTPClientProvider.displayName = `${name}HTTPClientProvider`;
  function useServiceHTTPClient() {
    const client = useContext(Context);
    if (!client) {
      throw new Error(
        `use${name}HTTPClient must be called inside of ${ServiceHTTPClientProvider.displayName}`
      );
    }
    return client;
  }
  return [ServiceHTTPClientProvider, useServiceHTTPClient];
}
function createServiceMutationHook({
  mutationFn,
  useClient
}) {
  function useServiceMutation(options) {
    const client = useClient();
    return useMutation({
      ...options,
      mutationFn: (params) => mutationFn({ params, client })
    });
  }
  return useServiceMutation;
}
function createBoundServiceMutationHookCreator({ useClient }) {
  function createBoundServiceMutationHook({
    mutationFn
  }) {
    return createServiceMutationHook({
      useClient,
      mutationFn
    });
  }
  return createBoundServiceMutationHook;
}
function createServiceQueryHooks({
  queryFn,
  queryKey,
  useClient
}) {
  function getQueryFn(options) {
    return () => queryFn(options);
  }
  function getQueryKey(options) {
    return typeof (options == null ? void 0 : options.params) !== "undefined" ? [...queryKey, options.params] : queryKey;
  }
  function useServiceQuery(options) {
    const client = useClient();
    const params = typeof options !== "undefined" ? options.params : void 0;
    return useQuery({
      ...options,
      queryFn: getQueryFn({ params, client }),
      queryKey: getQueryKey({ params })
    });
  }
  useServiceQuery.getQueryFn = getQueryFn;
  useServiceQuery.getQueryKey = getQueryKey;
  function useSuspenseServiceQuery(options) {
    const client = useClient();
    const params = typeof options !== "undefined" ? options.params : void 0;
    return useSuspenseQuery({
      ...options,
      queryFn: getQueryFn({ params, client }),
      queryKey: getQueryKey({ params })
    });
  }
  useSuspenseServiceQuery.getQueryFn = getQueryFn;
  useSuspenseServiceQuery.getQueryKey = getQueryKey;
  return [useServiceQuery, useSuspenseServiceQuery];
}
function createBoundServiceQueryHooksCreator({
  useClient,
  queryKeyPrefix
}) {
  function createBoundServiceQueryHooks({
    queryFn,
    queryKey
  }) {
    return createServiceQueryHooks({
      queryFn,
      queryKey: [queryKeyPrefix, ...queryKey],
      useClient
    });
  }
  return createBoundServiceQueryHooks;
}
export {
  createBoundServiceMutationHookCreator,
  createBoundServiceQueryHooksCreator,
  createServiceHTTPClientProvider,
  createServiceMutationHook,
  createServiceQueryHooks
};
//# sourceMappingURL=index.js.map
